import { faBars, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { FC, ReactNode } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, NoStyleButton } from "@aviary";
import { Container } from "@aviary/layouts";
import { SvgFullscriptLogoDarkTextGreenLeaf, SvgFullscriptIcon } from "@shared/assets/logo";
import { useBreakpoints } from "@shared/hooks";
import { openCatalogRoutes } from "@unauthenticated/catalog/data/openCatalogRoutes";
import { l } from "@unauthenticated/catalog/locales/i18n";

import * as styles from "./OpenMobileNavContainer.styles";

interface Props {
  children: ReactNode;
}

const OpenMobileNavContainer: FC<Props> = ({ children, ...rest }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { tablet, phoneSmall } = useBreakpoints();

  const getLogo = () => {
    return (
      <Button
        noStyle
        css={styles.homeLink}
        href={openCatalogRoutes.fullscript}
        aria-label={t(l.navigation.FullscriptLogo)}
      >
        {tablet.lessThan ? (
          <SvgFullscriptIcon css={styles.navLogo} />
        ) : (
          <SvgFullscriptLogoDarkTextGreenLeaf css={styles.navLogo} />
        )}
      </Button>
    );
  };

  const getCloseIcon = () => {
    return (
      <NoStyleButton
        aria-label={t(l.navigation.CloseNavigation)}
        onClick={() => toggleNav()}
        css={styles.closeIconContainer}
      >
        <FontAwesomeIcon icon={faTimes} size="lg" />
      </NoStyleButton>
    );
  };

  const getHamburgerMenu = () => {
    return (
      <NoStyleButton
        aria-label={t(l.navigation.OpenNavigation)}
        onClick={() => toggleNav()}
        css={styles.sidebarToggle}
        {...rest}
      >
        <FontAwesomeIcon css={styles.hamburger} icon={faBars} size="lg" />
      </NoStyleButton>
    );
  };

  const getLoginButton = () => {
    return (
      <Button isOutlined href={openCatalogRoutes.login} css={styles.loginButton}>
        {t(l.navigation.SignIn)}
      </Button>
    );
  };

  const toggleNav = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div css={[styles.mainNav]}>
      <Container css={[styles.container, phoneSmall.lessThan && styles.mobileMenu]}>
        {getLogo()}
        <div css={styles.mobileNavContainer}>
          {getLoginButton()}
          {isOpen ? getCloseIcon() : getHamburgerMenu()}
        </div>
      </Container>
      <div css={[styles.nav, isOpen ? styles.navOpened : styles.navClosed]}>{children}</div>
    </div>
  );
};

export { OpenMobileNavContainer };
